/**
 * Utility functions for content manipulation - copy, export, etc.
 */

import { Summary } from '../types';

/**
 * Copy text to clipboard with fallback for older browsers
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    // Modern approach using Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers or non-secure contexts
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    return successful;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Export content as a downloadable text file
 */
export function exportAsTextFile(content: string, filename: string = 'summary.txt'): void {
  try {
    if (!content || content.trim().length === 0) {
      console.error('❌ Export failed: No content provided');
      throw new Error('No content to export');
    }

    // Try primary method first
    try {
      exportUsingBlob(content, filename);
    } catch (blobError) {
      console.warn('⚠️ Blob method failed, trying fallback:', blobError);
      exportUsingDataUri(content, filename);
    }
  } catch (error) {
    console.error('❌ Failed to export file:', error);
    throw new Error(
      `Failed to export file: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Primary export method using Blob API
 */
function exportUsingBlob(content: string, filename: string): void {
  // Create blob with the content
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });

  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  // Trigger download
  document.body.appendChild(link);

  // Use a timeout to ensure the link is added to DOM before clicking
  setTimeout(() => {
    link.click();

    // Cleanup after a short delay to ensure download starts
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      URL.revokeObjectURL(url);
    }, 100);
  }, 10);
}

/**
 * Fallback export method using Data URI
 */
function exportUsingDataUri(content: string, filename: string): void {
  // Encode content as data URI
  const dataStr = 'data:text/plain;charset=utf-8,' + encodeURIComponent(content);
  const link = document.createElement('a');
  link.href = dataStr;
  link.download = filename;
  link.style.display = 'none';

  // Trigger download
  document.body.appendChild(link);
  setTimeout(() => {
    link.click();
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
    }, 100);
  }, 10);
}

/**
 * Modern export method with better browser support
 */
export function exportAsTextFileModern(
  content: string,
  filename: string = 'summary.txt'
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      if (!content || content.trim().length === 0) {
        reject(new Error('No content to export'));
        return;
      }

      // Check if File System Access API is available (Chrome 86+)
      if ('showSaveFilePicker' in window) {
        exportUsingFileSystemAPI(content, filename)
          .then(resolve)
          .catch(() => {
            // Fallback to traditional method
            exportAsTextFile(content, filename);
            resolve();
          });
      } else {
        // Use traditional method
        exportAsTextFile(content, filename);
        resolve();
      }
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Export using modern File System Access API
 */
async function exportUsingFileSystemAPI(content: string, filename: string): Promise<void> {
  try {
    // @ts-ignore - File System Access API types might not be available
    const fileHandle = await window.showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: 'Text files',
          accept: { 'text/plain': ['.txt'] },
        },
      ],
    });

    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();
  } catch (error) {
    console.warn('⚠️ File System Access API failed:', error);
    throw error;
  }
}

/**
 * Format summary content for export
 */
export function formatSummaryForExport(summary: Summary): string {
  const sections: string[] = [];

  // Add title
  if (summary.title) {
    sections.push(`Title: ${summary.title}`);
    sections.push('='.repeat(50));
    sections.push('');
  }

  // Add source information
  if (summary.source) {
    sections.push(`Source: ${summary.source}`);
  }
  if (summary.created_at) {
    sections.push(`Generated: ${new Date(summary.created_at).toLocaleString()}`);
  }
  sections.push('');

  // Add main summary
  if (summary.summary_as_md || summary.summary) {
    sections.push('Summary:');
    sections.push('-'.repeat(20));
    sections.push(summary.summary_as_md || summary.summary || '');
    sections.push('');
  }

  // Add transcript if available
  if (summary.transcript) {
    sections.push('Transcript:');
    sections.push('-'.repeat(20));
    sections.push(summary.transcript);
    sections.push('');
  }

  // Add footer
  sections.push('-'.repeat(50));
  sections.push('Generated with TubeOnAI - https://tubeonai.com');

  return sections.join('\n');
}

/**
 * Format transcript for export
 */
export function formatTranscriptForExport(transcript: string, title?: string): string {
  const sections: string[] = [];

  if (title) {
    sections.push(`Title: ${title}`);
    sections.push('='.repeat(50));
    sections.push('');
  }

  sections.push('Transcript:');
  sections.push('-'.repeat(20));
  sections.push(transcript);
  sections.push('');
  sections.push('-'.repeat(50));
  sections.push('Generated with TubeOnAI - https://tubeonai.com');

  return sections.join('\n');
}

/**
 * Strip HTML tags from text
 */
export function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

/**
 * Generate filename based on summary title and date
 */
export function generateFilename(title?: string, extension: string = 'txt'): string {
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const baseTitle = title
    ? title
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()
        .substring(0, 50)
    : 'summary';
  return `${baseTitle}_${date}.${extension}`;
}

/**
 * Export content as a downloadable PDF file using the text-to-pdf API
 */
export async function exportAsPDF(
  content: string,
  title: string,
  filename?: string
): Promise<void> {
  try {
    if (!content || content.trim().length === 0) {
      throw new Error('No content to export');
    }

    // Make API call to text-to-pdf endpoint
    const response = await fetch('https://web.tubeonai.com/api/text-to-pdf', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: title || 'Summary',
        text: content,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to generate PDF: ${response.statusText}`);
    }

    // Get the PDF blob from response
    const pdfBlob = await response.blob();

    // Generate filename if not provided
    const pdfFilename = filename || generateFilename(title, 'pdf');

    // Create download link
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = pdfFilename;
    link.style.display = 'none';

    // Trigger download
    document.body.appendChild(link);
    setTimeout(() => {
      link.click();

      // Cleanup after download
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
      }, 100);
    }, 10);
  } catch (error) {
    console.error('Failed to export PDF:', error);
    throw new Error(
      `Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
